// Considered: the two places this library already paints a magnitude — SatelliteCardAtom's
// circular SVG gravity ring (bound to a sphere's gravity, not a reusable track) and
// PasswordStrengthIndicatorMolecule's hand-rolled `__fill` bar (a molecule, four fixed strength
// steps, no value API). Neither is a reusable determinate track, so this atom is the design
// system's one progress track. PasswordStrengthIndicator is a candidate consumer — recorded in
// .ralph/IMPLEMENTATION_PLAN.md, out of scope here.
//
// It owns its own two-element markup instead of wrapping `primereact/progressbar`: PrimeReact
// paints the fill's magnitude as an inline `width` and transitions it (`transition: width 1s`) —
// a layout property — and sweeps its indeterminate bar on `left`/`right` for 2.1 s with no
// `prefers-reduced-motion` guard. Those are ux-craft.md M3 and M4 errors that can only be
// "overridden" by fighting inline styles with `!important`. Driving a `scaleX` fill from a custom
// property satisfies M1–M4 directly, and the semantics come from a visually hidden native `<progress>`, and the painted track is `aria-hidden`.
import React from 'react';
import './ProgressBarAtom.scss';

export type ProgressBarTone = 'primary' | 'success' | 'warning' | 'danger' | 'info';
export type ProgressBarSize = 'small' | 'normal' | 'large';

export interface ProgressBarAtomProps {
  /**
   * Completion percentage, 0-100. Clamped here as well as by callers, so an out-of-range
   * value can never paint a fill wider than the track or report an impossible
   * `aria-valuenow`. Ignored when `indeterminate` is set; defaults to 0 so an
   * indeterminate bar needs no value.
   */
  value?: number;
  /**
   * A bar for work whose total is not yet known (a saga whose DAG is still being computed,
   * an organization still being prepared): no magnitude, no `aria-valuenow`. Default false.
   */
  indeterminate?: boolean;
  /** Semantic colour of the filled track. Default `primary`. */
  tone?: ProgressBarTone;
  /** Track height. Default `normal`. */
  size?: ProgressBarSize;
  /** Render the numeric percentage beside the track. Default true. */
  showValue?: boolean;
  /** Accessible name for the progress track — required since no visible label is guaranteed (the caller-facing `label` prop this atom's consumers pass is rendered separately, not associated with the track itself). */
  ariaLabel?: string;
  className?: string;
  'data-testid'?: string;
}

const ProgressBarAtom: React.FC<ProgressBarAtomProps> = ({
  value = 0,
  indeterminate = false,
  tone = 'primary',
  size = 'normal',
  showValue = true,
  ariaLabel,
  className = '',
  'data-testid': testId,
}) => {
  const clamped = Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : 0;

  return (
    <div
      className={[
        'progress-bar-atom',
        `progress-bar-atom--${tone}`,
        `progress-bar-atom--${size}`,
        indeterminate ? 'progress-bar-atom--indeterminate' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid={testId}
    >
      <progress
        className="progress-bar-atom__native"
        max={100}
        value={indeterminate ? undefined : clamped}
        aria-label={ariaLabel}
      />
      <div className="progress-bar-atom__track" aria-hidden="true">
        <div
          className="progress-bar-atom__fill"
          // The magnitude is a unitless scale factor, not a length: the fill spans the track
          // and `scaleX` shortens it, so the bar moves on `transform` only (ux-craft M3).
          style={
            indeterminate ? undefined : ({ '--progress-bar-fill-scale': clamped / 100 } as React.CSSProperties)
          }
        />
      </div>
      {!indeterminate && showValue && (
        // Beside the track, not inside the fill: a `scaleX`'d fill would squash its own text,
        // and PrimeReact's in-fill label clipped below ~20% and put light text on whichever
        // tone happened to be underneath it (ux-craft A5 contrast).
        <span className="progress-bar-atom__label">{`${clamped}%`}</span>
      )}
    </div>
  );
};

export default ProgressBarAtom;
